import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck, IconLeaf, IconPillFilled } from "@tabler/icons-react";
import MDialog from "@/components/ui/atoms/MDialog";
import { MedicineItemType, MedicineStatus, PillboxItem } from "@/types/pillbox";
import {
  formatFrequencyLine,
  formatMedicineTitle,
} from "../utils/formatMedicine";
import styles from "./DuplicateMedicineModal.module.css";

export interface DuplicateMedicineModalProps {
  open: boolean;
  /** One item -> today's plain "this already exists" preview. More than one
   * (e.g. typing "Crocin" when several "Crocin ___" variants exist) -> a
   * selectable list, with an "add as new" escape hatch. */
  items: PillboxItem[];
  memberDisplayName: string;
  onClose: () => void;
  onConfirm: (item: PillboxItem) => void;
  /** Only relevant/rendered when items.length > 1. */
  onAddNew?: () => void;
  /** Reflects async work the confirm/add-new choice kicked off in the
   * caller (e.g. rejecting an OCR-derived pending change before routing to
   * the matched existing item) — disables both CTAs while true. */
  loading?: boolean;
}

const DuplicateMedicineModal: React.FC<DuplicateMedicineModalProps> = ({
  open,
  items,
  memberDisplayName,
  onClose,
  onConfirm,
  onAddNew,
  loading,
}) => {
  const { t } = useTranslation();
  const isMultiple = items.length > 1;
  // Only meaningful for the single-item case — a multi-item list can mix
  // active/past freely (see AddMedicine's combined matching) and always
  // shows the same "pick one" copy regardless of each row's status.
  const isActive = items[0]?.status === MedicineStatus.ACTIVE;
  const [selectedId, setSelectedId] = useState<string | null>(
    items.length === 1 ? items[0].id : null
  );

  // Reset selection whenever the drawer is (re)opened with a new candidate set.
  useEffect(() => {
    setSelectedId(items.length === 1 ? items[0].id : null);
  }, [items]);

  const selectedItem = items.find((i) => i.id === selectedId) ?? null;

  const renderRowContent = (item: PillboxItem) => {
    const isSupplement = item.itemType === MedicineItemType.SUPPLEMENT;
    return (
      <>
        <span className={styles.iconBox}>
          {isSupplement ? (
            <IconLeaf size={24} stroke={1.5} color="#45168C" />
          ) : (
            <IconPillFilled size={24} color="#45168C" />
          )}
        </span>
        <span className={styles.content}>
          <span className={styles.title}>{formatMedicineTitle(item)}</span>
          <span className={styles.frequencyLine}>
            {formatFrequencyLine(item, item.recurrenceIntervalDays)}
          </span>
        </span>
      </>
    );
  };

  return (
    <MDialog
      isOpen={open}
      onClose={onClose}
      title={
        isMultiple
          ? t("pillbox.duplicateModal.multipleTitle")
          : t("pillbox.duplicateModal.title", { member: memberDisplayName })
      }
      description={t(
        isMultiple
          ? "pillbox.duplicateModal.selectPrompt"
          : isActive
          ? "pillbox.duplicateModal.bodyActive"
          : "pillbox.duplicateModal.bodyPast"
      )}
      primaryCtaText={t(
        isMultiple
          ? "pillbox.duplicateModal.continueButton"
          : isActive
          ? "pillbox.duplicateModal.editButton"
          : "pillbox.duplicateModal.restoreButton"
      )}
      onPrimaryCta={() => selectedItem && onConfirm(selectedItem)}
      isPrimaryCtaDisabled={!selectedItem}
      isPrimaryCtaLoading={loading}
      secondaryCtaText={
        isMultiple ? t("pillbox.duplicateModal.addAsNew") : undefined
      }
      onSecondaryCta={isMultiple ? onAddNew : undefined}
    >
      {isMultiple ? (
        <div className={styles.list}>
          {items.map((item) => {
            const isSelected = selectedId === item.id;
            return (
              <button
                type="button"
                key={item.id}
                className={`${styles.previewCard} ${styles.selectable}${
                  isSelected ? ` ${styles.selected}` : ""
                }`}
                onClick={() => setSelectedId(item.id)}
              >
                {renderRowContent(item)}
                {isSelected && (
                  <IconCheck
                    size={20}
                    color="#45168C"
                    className={styles.checkIcon}
                  />
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className={styles.previewCard}>{renderRowContent(items[0])}</div>
      )}
    </MDialog>
  );
};

export default DuplicateMedicineModal;
